import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Bell, CheckSquare, ClipboardList, AlertTriangle, FileText, FolderKanban,
  CalendarDays, X, Check, Trash2,
} from 'lucide-react';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';

// Only enabled, record-relevant modules get a filter chip + navigation.
// (Contracts + disabled modules intentionally excluded.)
const CATEGORY = {
  project: 'Projects',
  delinquency_project: 'Projects',
  delinquency_project_mgr: 'Projects',
  task_assigned: 'Tasks',
  inspection_reminder: 'Inspections',
  fdny_inspection_followup: 'Inspections',
  dot_permit_expiration: 'Expiration Alerts',
  dob_filing_expiration: 'Expiration Alerts',
  proposal: 'Status Changes',
  calendar_reminder: 'Reminders',
};
const LINKABLE = new Set(Object.keys(CATEGORY));

const CAT_ICON = {
  Tasks: CheckSquare,
  Inspections: ClipboardList,
  'Expiration Alerts': AlertTriangle,
  'Status Changes': FileText,
  Projects: FolderKanban,
  Reminders: CalendarDays,
};

const catOf = (n) => CATEGORY[n?.entity_type] || null;

export default function NotificationCenter() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('All');

  const load = useCallback(() => {
    api.get('/notifications').then(r => setNotifications(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const readCount = notifications.filter(n => n.is_read).length;

  const markRead = async (n) => {
    if (!n.is_read) {
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      api.put(`/notifications/${n.id}/read`).catch(() => {});
    }
  };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    api.put('/notifications/read-all').catch(() => {});
  };

  const clearAllRead = async () => {
    const ok = await confirm({
      title: 'Clear all read notifications',
      description: `Remove all ${readCount} read notification(s)? Unread notifications are kept.`,
      confirmLabel: 'Clear',
      destructive: true,
    });
    if (!ok) return;
    setNotifications(prev => prev.filter(n => !n.is_read));
    api.delete('/notifications/read').catch(() => {});
  };

  const removeNotif = (e, n) => {
    e.stopPropagation();
    setNotifications(prev => prev.filter(x => x.id !== n.id));
    api.delete(`/notifications/${n.id}`).catch(() => {});
  };

  const handleClick = async (n) => {
    await markRead(n);
    if (LINKABLE.has(n.entity_type)) {
      try {
        const r = await api.get(`/notifications/${n.id}/link`);
        if (r.data?.route) {
          setOpen(false);
          navigate(r.data.route);
        }
      } catch { /* no navigable target */ }
    }
  };

  const categoriesPresent = Array.from(new Set(notifications.map(catOf).filter(Boolean)));
  const chips = ['All', ...categoriesPresent];
  const visible = filter === 'All' ? notifications : notifications.filter(n => catOf(n) === filter);

  // Recency grouping for an inbox feel
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const weekAgo = new Date(startOfToday.getTime() - 6 * 86400000);
  const groupOf = (n) => {
    const d = new Date(n.created_at);
    if (isNaN(d.getTime())) return 'Older';
    if (d >= startOfToday) return 'Today';
    if (d >= weekAgo) return 'Earlier this week';
    return 'Older';
  };
  const grouped = ['Today', 'Earlier this week', 'Older']
    .map(label => ({ label, items: visible.filter(n => groupOf(n) === label) }))
    .filter(g => g.items.length > 0);

  const renderNotif = (n) => {
    const cat = catOf(n);
    const Icon = (cat && CAT_ICON[cat]) || Bell;
    const linkable = LINKABLE.has(n.entity_type);
    const unread = !n.is_read;
    return (
      <div
        key={n.id}
        onClick={() => handleClick(n)}
        className={`group flex items-start gap-3 px-4 py-3 border-b border-border last:border-0 transition-colors ${linkable ? 'cursor-pointer' : ''} ${unread ? 'bg-primary/5 border-l-2 border-l-[#E5441A]' : 'hover:bg-secondary/40'}`}
        data-testid={`notification-item-${n.id}`}
      >
        <div className={`mt-0.5 shrink-0 ${unread ? 'text-primary' : 'text-muted-foreground/50'}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-1.5">
            {unread && <span className="mt-[5px] h-2 w-2 rounded-full bg-[#E5441A] shrink-0" data-testid={`notif-unread-dot-${n.id}`} />}
            <p className={`text-sm ${unread ? 'font-semibold text-[#010A21]' : 'font-normal text-muted-foreground'}`}>{n.message}</p>
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {cat && <><span className="text-xs text-muted-foreground">{cat}</span><span className="text-xs text-muted-foreground/50">·</span></>}
            <span className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleDateString()} {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
        <button
          onClick={(e) => removeNotif(e, n)}
          className="shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
          data-testid={`notif-clear-${n.id}`}
          aria-label="Clear notification"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  return (
    <>
      <Button
        variant="ghost" size="icon"
        className="relative h-8 w-8 text-[#E8EDF2] hover:text-white hover:bg-[#1B4A76]"
        onClick={() => setOpen(true)}
        data-testid="notification-bell"
      >
        <Bell className="h-[18px] w-[18px]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-xs font-semibold flex items-center justify-center text-primary-foreground" data-testid="notification-unread-count">
            {unreadCount}
          </span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-0 gap-0 bg-card border-border overflow-hidden" aria-describedby={undefined} data-testid="notifications-modal">
          <DialogHeader className="px-5 py-3 border-b border-border space-y-0" style={{ backgroundColor: '#132C4A' }}>
            <DialogTitle className="text-sm text-white flex items-center gap-2">
              <Bell className="h-4 w-4" /> Notifications
              {unreadCount > 0 && <span className="text-xs font-normal" style={{ color: '#A9C2DB' }}>({unreadCount} unread)</span>}
            </DialogTitle>
          </DialogHeader>

          <div className="flex" style={{ minHeight: '360px' }}>
            {/* LEFT RAIL - filters + bulk actions */}
            <aside
              className="shrink-0 flex flex-col justify-between py-4"
              style={{ width: '208px', backgroundColor: '#F5F8FB', borderRight: '1px solid #E2E8F0' }}
              data-testid="notif-left-rail"
            >
              <div className="px-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide px-2 mb-1.5" style={{ color: '#5A7A99' }}>Filters</p>
                <div className="flex flex-col gap-0.5" data-testid="notif-filters">
                  {chips.map(c => {
                    const active = filter === c;
                    return (
                      <button
                        key={c}
                        onClick={() => setFilter(c)}
                        className="text-left text-sm rounded-md px-2.5 py-1.5 transition-colors"
                        style={active
                          ? { backgroundColor: '#132C4A', color: '#FFFFFF', fontWeight: 500 }
                          : { color: '#010A21' }}
                        onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = '#E4ECF3'; }}
                        onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        data-testid={`notif-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              {(unreadCount > 0 || readCount > 0) && (
                <div className="px-3 mt-4">
                  <div style={{ borderTop: '1px solid #E2E8F0' }} className="mb-2.5" />
                  <p className="text-[11px] font-semibold uppercase tracking-wide px-2 mb-1.5" style={{ color: '#5A7A99' }}>Actions</p>
                  <div className="flex flex-col gap-0.5">
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        className="flex items-center gap-2 text-left text-sm rounded-md px-2.5 py-1.5 transition-colors hover:bg-[#E4ECF3]"
                        style={{ color: '#1B4A76' }}
                        data-testid="notif-mark-all-read"
                      >
                        <Check className="h-3.5 w-3.5" /> Mark all read
                      </button>
                    )}
                    {readCount > 0 && (
                      <button
                        onClick={clearAllRead}
                        className="flex items-center gap-2 text-left text-sm rounded-md px-2.5 py-1.5 transition-colors hover:bg-[#FDECEA] hover:text-[#E5441A]"
                        style={{ color: '#5A7A99' }}
                        data-testid="notif-clear-all-read"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Clear read
                      </button>
                    )}
                  </div>
                </div>
              )}
            </aside>

            {/* RIGHT - notification list */}
            <div className="flex-1 min-w-0 max-h-[60vh] overflow-y-auto" data-testid="notif-list">
              {visible.length === 0 ? (
                <div className="px-4 py-16 text-center text-sm text-muted-foreground">No notifications</div>
              ) : (
                grouped.map(group => (
                  <div key={group.label} data-testid={`notif-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="sticky top-0 z-10 flex items-center gap-1.5 px-4 py-1.5 bg-secondary/70 backdrop-blur-sm border-b border-border">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</span>
                      <span className="text-[11px] text-muted-foreground/60">({group.items.length})</span>
                    </div>
                    {group.items.map(renderNotif)}
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
